import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CalendarDays, Camera, ChevronRight, Clock, RefreshCw, Search, ShoppingBasket, Sparkles } from 'lucide-react';
import { BasicsNudge } from '../components/BasicsNudge';
import { kitchenNote, MealCard, MissingRow, SaveHeart } from '../components/MealCards';
import { PromptCard } from '../components/PromptCard';
import { RecipeImage } from '../components/RecipeImage';
import { ReminderCard, useReminderOffer } from '../components/ReminderCard';
import { newItem } from '../domain/grocery';
import { almostThere, dailyPicks, type Recommendation } from '../domain/recommend';
import type { MealType } from '../domain/recipes';
import { getRecipe } from '../domain/recipes';
import { displayName } from '../domain/taxonomy';
import { capitalize, joinNatural, MAX_QUERY, sanitizeInput } from '../domain/text';
import { checkInDue } from '../domain/checkin';
import { track } from '../lib/analytics';
import { addDays, todayISO } from '../lib/dates';
import { useRecommendations } from '../state/selectors';
import { useStore } from '../state/store';
import type { SkipReason } from '../state/schema';
import { AmariLogo } from '../components/Logo';
import { AskAmariCard } from '../components/AskEntry';

const SKIP_REASONS: { id: SkipReason; label: string }[] = [
  { id: 'no_time', label: 'No time' },
  { id: 'not_in_mood', label: 'Wasn’t in the mood' },
  { id: 'missing_ingredient', label: 'Missing an ingredient' },
  { id: 'ate_out', label: 'Ate out or ordered in' },
  { id: 'didnt_fancy', label: 'Didn’t fancy it' },
  { id: 'other', label: 'Something else' },
];

function greeting(now = new Date()): string {
  const h = now.getHours();
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
}

/** "Meals you can make" covers every meal type, not only dinners. */
const ALL_MEALS: MealType[] = ['main', 'breakfast', 'side', 'snack'];

/**
 * Home answers one question at a glance: what's for dinner tonight? Three picks lead (A is the
 * best, B and C are different enough to be real choices), so there's no list to weigh up. Below
 * them sits EVERY meal the kitchen covers right now, then the ones a short shop away. Plan and List
 * live in the header, settings in Profile.
 */
export function Today() {
  const navigate = useNavigate();
  const dinners = useRecommendations();
  const everything = useRecommendations(ALL_MEALS);
  const kitchen = useStore((s) => s.kitchen);
  const plan = useStore((s) => s.plan);
  const list = useStore((s) => s.list);
  const profile = useStore((s) => s.profile);
  const addListItems = useStore((s) => s.addListItems);
  const recordFeedback = useStore((s) => s.recordFeedback);
  const showToast = useStore((s) => s.showToast);
  const checkIns = useStore((s) => s.checkIns);
  const snoozed = useStore((s) => s.checkInSnoozedUntil);
  const onboardedAt = useStore((s) => s.onboardedAt);
  const offerReminders = useReminderOffer();
  const [query, setQuery] = useState('');
  const [round, setRound] = useState(0);

  const today = todayISO();
  const known = kitchen.items.length > 0;
  const planned = plan?.entries.find((e) => e.date === today && e.status === 'planned');
  const yesterday = plan?.entries.find((e) => e.date === addDays(today, -1) && e.status === 'planned');
  const due = checkInDue({ intervalMonths: profile.checkInMonths, startedAt: onboardedAt, history: checkIns, snoozedUntil: snoozed }, today);
  const openItems = list.filter((i) => !i.checked).length;

  // Tonight's planned dinner leads the three; the rest rotate daily among the best fits.
  const lead = planned ? dinners.find((r) => r.recipe.id === planned.recipeId) : undefined;
  const picks = useMemo(() => dailyPicks(dinners, today, { round, lead }), [dinners, today, round, lead]);
  const ready = useMemo(() => (known ? everything.filter((r) => r.makeable) : []), [everything, known]);
  const almost = useMemo(() => (known ? almostThere(dinners, 6).filter((r) => !picks.some((x) => x.recipe.id === r.recipe.id)).slice(0, 3) : []), [dinners, known, picks]);
  const onList = (r: Recommendation) => r.missing.every((m) => list.some((l) => l.key === m && !l.checked));

  useEffect(() => {
    if (picks[0]) track('recipe_shown', { recipe: picks[0].recipe.id, makeable: picks[0].makeable, from_plan: !!lead, ready: ready.length });
  }, [picks[0]?.recipe.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const addMissing = (r: Recommendation) => {
    const n = addListItems(r.missing.map((id) => ({ ...newItem(id, 'manual'), recipeIds: [r.recipe.id] })));
    recordFeedback({ type: 'chose', recipeId: r.recipe.id, surface: 'almost' });
    showToast(n ? `Added ${joinNatural(r.missing.map(displayName))} to your list` : 'Already on your list', { action: { label: 'View', run: () => navigate('/list') } });
  };
  const search = (e: FormEvent) => {
    e.preventDefault();
    navigate(`/more/ideas${query.trim() ? `?q=${encodeURIComponent(query.trim())}` : ''}`);
  };
  const others = () => { track('picks_shuffled', { round: round + 1 }); setRound((n) => n + 1); };

  // One gentle card at a time, most time-sensitive first.
  const notice = yesterday ? <LastNight entryDate={yesterday.date} recipeId={yesterday.recipeId} />
    : due ? (
      <Link to="/you/check-in" className="tile tile-interactive flex items-center gap-3 px-4 py-3 mb-6 min-h-[60px]">
        <span className="flex-1 text-subhead"><span className="font-semibold">Time for a quick check-in.</span> See how things are going.</span>
        <ChevronRight size={18} className="text-fg-muted" aria-hidden />
      </Link>
    ) : offerReminders ? <ReminderCard /> : <PromptCard />;

  return (
    <main className="screen screen-soft screen-tabs">
      <div className="max-w-[720px] mx-auto">
        <header className="flex items-center gap-2 pt-1 mb-5">
          <p className="flex-1"><AmariLogo /></p>
          <Link to="/plan" className="icon-btn btn-glass" aria-label="Weekly plan"><CalendarDays size={21} aria-hidden /></Link>
          <Link to="/list" className="icon-btn btn-glass relative" aria-label={openItems ? `Shopping list, ${openItems} to buy` : 'Shopping list'}>
            <ShoppingBasket size={21} aria-hidden />
            {openItems > 0 && <span className="absolute -top-1 -right-1 min-w-[20px] h-[20px] px-1 rounded-full bg-primary text-primary-on text-[11px] font-bold flex items-center justify-center tabular" aria-hidden>{openItems > 99 ? '99+' : openItems}</span>}
          </Link>
        </header>

        <section className="flex items-center gap-4 mb-5">
          <div className="flex-1 min-w-0">
            <h1 className="text-title-2 font-bold">{greeting()}{profile.name ? `, ${profile.name}` : ''}</h1>
            <p className="text-subhead text-fg-muted mt-0.5">{known ? 'Dinner’s sorted. Pick one and go.' : 'Here are three ideas. Snap your food for meals you can make now.'}</p>
          </div>
          <Link to="/scan" className="btn btn-primary shrink-0 rounded-[18px] min-h-[56px] px-4 shadow-[0_6px_18px_rgb(var(--primary)/0.28)]">
            <Camera size={22} aria-hidden /> <span className="leading-tight text-left">Scan<br />your food</span>
          </Link>
        </section>

        <AskAmariCard from="today" className="mb-6" detail="What to cook, swaps, leftovers, your week" />

        {picks.length > 0 ? (
          <section className="mb-8" aria-labelledby="tonight">
            <div className="flex items-baseline mb-3">
              <h2 id="tonight" className="section-title flex-1">What&rsquo;s for dinner tonight?</h2>
              <button type="button" className="link-more" onClick={others}><RefreshCw size={15} aria-hidden /> Others</button>
            </div>
            <DinnerPick r={picks[0]} letter="A" big label={lead ? 'From your plan' : picks[0].makeable ? 'Best pick tonight' : known ? 'Closest to what you have' : 'Tonight’s idea'} />
            {picks.length > 1 && (
              <div className="grid grid-cols-2 gap-3 mt-3">
                {picks.slice(1).map((r, i) => <DinnerPick key={r.recipe.id} r={r} letter={i ? 'C' : 'B'} />)}
              </div>
            )}
          </section>
        ) : (
          <p className="tile p-4 mb-8 text-subhead text-fg-muted">No dinners fit your allergies and diets yet. <Link to="/you/rules" className="text-primary font-semibold">Review them</Link></p>
        )}

        {known && <BasicsNudge />}

        {known ? (
          <section className="mb-8" aria-labelledby="can-make">
            <div className="flex items-center mb-1">
              <h2 id="can-make" className="section-title flex-1">{ready.length ? `Meals you can make (${ready.length})` : 'Meals you can make'}</h2>
              {ready.length > 0 && <Link to="/more/tonight" className="link-more">List <ChevronRight size={18} aria-hidden /></Link>}
            </div>
            {ready.length > 0 ? (
              <>
                <p className="text-subhead text-fg-muted mb-3">Everything your kitchen covers right now: breakfasts, dinners, sides and snacks.</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {ready.map((r) => <MealCard key={r.recipe.id} r={r} wide />)}
                </div>
              </>
            ) : (
              <p className="tile p-4 text-subhead text-fg-muted">Nothing is fully covered yet. The picks above need the fewest extras, and <a href="#almost" className="text-primary font-semibold">Almost there</a> shows what to buy.</p>
            )}
          </section>
        ) : (
          <Link to="/scan" className="tile tile-interactive flex items-center gap-4 p-4 mb-8 min-h-[76px]">
            <span className="w-12 h-12 rounded-[14px] bg-primary-container text-primary-on-container flex items-center justify-center shrink-0" aria-hidden><Sparkles size={22} /></span>
            <span className="flex-1"><span className="block text-headline">See every meal you can make</span><span className="block text-subhead text-fg-muted">Snap your fridge or type a few things you have.</span></span>
            <ChevronRight size={18} className="text-fg-muted" aria-hidden />
          </Link>
        )}

        {notice}

        {almost.length > 0 && (
          <section className="mb-8" aria-labelledby="almost" id="almost">
            <div className="flex items-center">
              <h2 id="almost-title" className="section-title flex-1">Almost there</h2>
              <Link to="/more/almost" className="link-more">See all <ChevronRight size={18} aria-hidden /></Link>
            </div>
            <p className="text-subhead text-fg-muted mb-3">Add a few ingredients to make these.</p>
            <div className="grid gap-3">
              {almost.map((r) => <MissingRow key={r.recipe.id} r={r} onAdd={() => addMissing(r)} onList={onList(r)} />)}
            </div>
          </section>
        )}

        <form role="search" onSubmit={search} className="search mb-6">
          <Search size={20} aria-hidden />
          <input type="search" aria-label="Search meals or ingredients" placeholder="Search all recipes or ingredients" maxLength={MAX_QUERY} value={query} onChange={(e) => setQuery(sanitizeInput(e.target.value, MAX_QUERY))} />
        </form>
      </div>
    </main>
  );
}

/** One of tonight's three. A is the big one; B and C sit side by side beneath it. */
function DinnerPick({ r, letter, big = false, label }: { r: Recommendation; letter: 'A' | 'B' | 'C'; big?: boolean; label?: string }) {
  const why = r.reasons[0] ?? kitchenNote(r, true);
  const note = kitchenNote(r, true);
  return (
    <Link to={`/recipe/${r.recipe.id}`} className="tile tile-interactive block overflow-hidden"
      aria-label={`Option ${letter}: ${r.recipe.title}, ${r.recipe.minutes} minutes${note ? `, ${note}` : ''}`}
      onClick={() => track('tonight_pick_opened', { recipe: r.recipe.id, option: letter })}>
      <span className="relative block">
        <RecipeImage recipeId={r.recipe.id} ratio={big ? '16/10' : '4/3'} priority={big} rounded="rounded-none" />
        <span className="absolute top-2.5 left-2.5 w-8 h-8 rounded-full btn-on-image flex items-center justify-center text-[15px] font-bold" aria-hidden>{letter}</span>
        <SaveHeart recipeId={r.recipe.id} className="absolute top-2 right-2" />
      </span>
      <span className={`block ${big ? 'p-4' : 'p-3'}`}>
        {big && label && <span className="block eyebrow text-primary mb-1">{label}</span>}
        <span className={`block ${big ? 'text-title-3' : 'text-headline leading-snug line-clamp-2 min-h-[44px]'}`}>{r.recipe.title}</span>
        <span className="flex items-center gap-1 text-footnote text-fg-muted mt-1">
          <Clock size={13} aria-hidden /> {r.recipe.minutes} min{note ? ` · ${note}` : ''}
        </span>
        {big && why && why !== note && <span className="block text-subhead text-fg-muted mt-1 line-clamp-2">{capitalize(why)}</span>}
      </span>
    </Link>
  );
}

/** Lightweight follow-up on yesterday's planned dinner, so skips are explained, not assumed. */
function LastNight({ entryDate, recipeId }: { entryDate: string; recipeId: string }) {
  const navigate = useNavigate();
  const skip = useStore((s) => s.skipPlanEntry);
  const [asking, setAsking] = useState(false);
  const recipe = getRecipe(recipeId);
  if (!recipe) return null;
  return (
    <section className="tile p-4 mb-6" aria-labelledby="last-night">
      <div className="flex items-center gap-3">
        <RecipeImage recipeId={recipeId} ratio="1/1" rounded="rounded-[10px]" className="w-12 shrink-0" />
        <h2 id="last-night" className="flex-1 text-subhead"><span className="font-semibold">Did you make {recipe.title}?</span> <span className="text-fg-muted">It was planned for last night.</span></h2>
      </div>
      {!asking ? (
        <div className="grid grid-cols-2 gap-2 mt-3">
          <button className="btn btn-sm btn-secondary" onClick={() => navigate(`/done/${recipeId}`)}>Yes, I did</button>
          <button className="btn btn-sm btn-secondary" onClick={() => setAsking(true)}>No</button>
        </div>
      ) : (
        <div className="mt-3">
          <p className="text-footnote text-fg-muted mb-2">What got in the way? This helps next week&rsquo;s plan.</p>
          <div className="flex flex-wrap gap-2">
            {SKIP_REASONS.map((x) => <button key={x.id} className="chip" onClick={() => skip(entryDate, x.id)}>{x.label}</button>)}
            <button className="chip bg-transparent text-fg-muted" onClick={() => skip(entryDate, null)}>Skip</button>
          </div>
        </div>
      )}
    </section>
  );
}
